import { useState, useEffect } from "react";
import Button from "../common/Button";
import Select from "../common/form/Select";
import * as Yup from "yup";
import toast from "react-hot-toast";
import request from "../../utils/fetch";
import {
  CheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import GenericForm from "../common/form/GenericForm";
import { general_info, misc_info, contact_info, districts } from "./configs";

const validationSchema = Yup.object().shape({
  Email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  verifyEmail: Yup.string()
    .oneOf([Yup.ref("Email"), null], "Emails must match")
    .required("Please verify your email"),
  LastName: Yup.string().nullable(),
  Company: Yup.string().nullable(),
  HomePhone: Yup.string()
    .matches(/^\+?\d{11}$/, "Invalid phone number, must be 10 digits")
    .nullable(),
  MobilePhone: Yup.string()
    .matches(/^\+?\d{11}$/, "Invalid phone number, must be 10 digits")
    .nullable(),
  BusinessPhone: Yup.string()
    .matches(/^\+?\d{11}$/, "Invalid phone number, must be 10 digits")
    .nullable(),
  Districts: Yup.array()
    .min(1, "At least one district is required")
    .required("District is required"),
});

const validateForm = (values) => {
  const errors = {};

  if (!values.LastName && !values.Company) {
    errors.LastName = "Either Last Name or Company is required";
    errors.Company = "Either Last Name or Company is required";
  }

  if (!values.HomePhone && !values.MobilePhone && !values.BusinessPhone) {
    errors.HomePhone = "At least one phone number is required";
    errors.MobilePhone = "At least one phone number is required";
    errors.BusinessPhone = "At least one phone number is required";
  }

  return errors;
};

export default function RegisterNewUser() {
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [registerFailure, setRegisterFailure] = useState(false);
  const [apiMessage, setApiMessage] = useState("");

  useEffect(() => {
    const handlePopState = (event) => {
      event.preventDefault();

      window.location.reload();
    };

    window.history.pushState(null, null, window.location.href);

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const initialValues = {
    FirstName: "",
    LastName: "",
    Company: "",
    HomePhone: "",
    MobilePhone: "",
    BusinessPhone: "",
    Address: "",
    Address2: "",
    City: "",
    State: "",
    ZipCode: "",
    Email: "",
    verifyEmail: "",
    Districts: [],
    Note: "",
  };

  const handleSubmit = async (values, { resetForm }) => {
    try {
      const response = await request({
        method: "post",
        url: `/register`,
        data: {
          ...values,
          Districts: values.Districts.map((district) => district.value),
        },
      });

      if (response === "Created") {
        setRegisterFailure(false);
        setRegisterSuccess(true);
        setApiMessage(response || "Registration successful!");
        toast.success("Form submitted successfully, " + response);
      } else {
        setRegisterSuccess(false);
        setRegisterFailure(true);
        setApiMessage(response.error_message || "An error occurred.");
      }

      resetForm();
    } catch (e) {
      setRegisterFailure(true);
      if (e.response && e.response.data && e.response.data.error_message) {
        setApiMessage(e.response.data.error_message);
      } else {
        setApiMessage(e.message || "An error occurred.");
      }
    }
  };

  if (registerSuccess) {
    return (
      <div className="p-6 text-center">
        <div>
          <img
            src="/PC_Horz_Color.svg"
            alt="logo"
            className="w-80 h-auto mx-auto mb-10"
          />
        </div>
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
          <CheckIcon
            className={"h-12 w-12 text-green-600"}
            aria-hidden="true"
          />
        </div>
        <h1 className="text-3xl font-bold">
          Your information has been recieved and is being reviewed by your
          district.{" "}
        </h1>
        <a href="/sign-in" className="text-blue-500 hover:underline mt-6 block">
          Back to sign-in
        </a>
      </div>
    );
  }

  if (registerFailure) {
    return (
      <div className="p-6 text-center">
        <div>
          <img
            src="/PC_Horz_Color.svg"
            alt="logo"
            className="w-80 h-auto mx-auto mb-10"
          />
        </div>
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <ExclamationTriangleIcon
            className="h-12 w-12 text-red-600"
            aria-hidden="true"
          />
        </div>
        <h1 className="text-3xl font-bold">{apiMessage}</h1>
        <h2 className="text-2xl font-bold">
          Please contact your district if you think this is an error.{" "}
        </h2>
        <a href="/sign-in" className="text-blue-500 hover:underline mt-6 block">
          Back to sign-in
        </a>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      <div className="mx-auto max-w-2xl px-4 pb-24 pt-16 sm:px-6 lg:max-w-7xl lg:px-8">
        <img
          src="/PC_Horz_Color.svg"
          alt="logo"
          className="mx-auto mb-10 "
          style={{ width: "500px", height: "auto" }}
          onClick={() => {
            window.location.reload();
          }}
        />
        <h1 className="text-3xl font-bold text-gray-900 text-center mb-6 -mt-48">
          Registration Form
        </h1>
        <span className="text-md text-gray-600 text-center block">
          Please fill out this form to request access from your district
        </span>
        <GenericForm
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validate={validateForm}
          validateOnBlur={false}
          validateOnChange={false}
          className="lg:gap-x-12 xl:gap-x-16"
        >
          {(formik, renderInputField) => (
            <>
              <div className="lg:min-h-screen col-span-1">
                <div className="mt-10 border-t border-gray-200 pt-10">
                  <h2 className="text-lg font-medium text-gray-900">
                    General information
                  </h2>
                  <div className="flex flex-col gap-2 mt-2">
                    {general_info.map(renderInputField)}
                  </div>
                </div>
                <div className="mt-10 border-t border-gray-200 pt-10">
                  <h2 className="text-lg font-medium text-gray-900">Misc</h2>
                  <div className="flex flex-col gap-2 mt-2">
                    {misc_info.map(renderInputField)}
                  </div>
                </div>
                <div className="mt-10 border-t border-gray-200 pt-10 flex justify-center hidden lg:flex">
                  <Button
                    type="submit"
                    className="h-12 w-full lg:w-96 px-10 py-3 text-lg"
                  >
                    Submit
                  </Button>
                </div>
              </div>
              <div className="lg:min-h-screen col-span-1">
                <div className="mt-10 border-t  border-gray-200 pt-10">
                  <h2 className="text-lg font-medium text-gray-900">
                    Contact information
                  </h2>
                  <div className="flex flex-col gap-2 mt-2">
                    {contact_info.map(renderInputField)}
                  </div>
                </div>
                <div className="mt-10 border-t border-gray-200 pt-10">
                  <Select
                    extraOptions={districts}
                    label="Select District"
                    isMulti
                    defaultValue={[]}
                    onChange={(newSelectedDistricts) => {
                      formik.setFieldValue("Districts", newSelectedDistricts);
                    }}
                  />
                  {formik.errors.Districts && formik.touched.Districts ? (
                    <div className="text-red-500 text-sm mt-2">
                      {formik.errors.Districts}
                    </div>
                  ) : null}
                </div>
                <div className="mt-10 border-t border-gray-200 pt-10 flex justify-center lg:hidden md:midden">
                  <Button
                    type="submit"
                    className="h-12 w-full lg:w-96 px-10 py-3 text-lg"
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </>
          )}
        </GenericForm>
      </div>
    </div>
  );
}
